import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { db } from "config/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  onSnapshot,
  Query,
  DocumentData,
  Unsubscribe,
} from "firebase/firestore";
import { setAllGames, setGamesLoading, setGamesError } from "store/slices/gamesSlice";
import { Game } from "types";
import type { RootState } from "store";

import * as geofire from "geofire-common";

interface UseUnifiedGameListOptions {
  distance?: number;
  includePublic?: boolean;
  includePrivate?: boolean;
}

const useUnifiedGameList = (options: UseUnifiedGameListOptions = {}): void => {
  const {
    distance = 10,
    includePublic = true,
    includePrivate = true,
  } = options;

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const locationState = useSelector((state: RootState) => state.location);
  const { country, lat, lng } = locationState || {};

  useEffect(() => {
    if (!user) {
      dispatch(setAllGames({}));
      return;
    }

    dispatch(setGamesLoading(true));
    dispatch(setGamesError(null));

    const listeners: Unsubscribe[] = [];
    const allGames = new Map<string, Game>();

    // Function to update combined games data
    const updateAllGames = () => {
      const gamesObject: Record<string, Game> = {};
      allGames.forEach((game, id) => {
        gamesObject[id] = game;
      });
      dispatch(setAllGames(gamesObject));
    };

    // Setup public games queries with geohash if location available
    if (includePublic && country && lat !== null && lng !== null) {
      let radiusInM = distance * 1000;
      if (["United States", "Liberia", "Myanmar"].includes(country)) {
        const MiPerKm = 0.621371;
        radiusInM *= MiPerKm;
      }
      const center: [number, number] = [lat, lng];
      const bounds = geofire.geohashQueryBounds(center, radiusInM);

      // Create queries for all public games (both cash and tournament)
      const publicGameRefs: Query<DocumentData>[] = [];
      for (const b of bounds) {
        const publicGameRef = query(
          collection(db, "games"),
          where("access", "==", "Public"),
          where("visible", "==", true),
          orderBy("geohash"),
          startAt(b[0]),
          endAt(b[1]),
        );
        publicGameRefs.push(publicGameRef);
      }

      // Set up listeners for each geohash bound
      publicGameRefs.forEach((ref, index) => {
        const unsubscribe = onSnapshot(
          ref,
          (res) => {
            try {
              // Remove games from this bound first
              const keysToRemove: string[] = [];
              allGames.forEach((game, id) => {
                if (game.access === "Public" && game._boundIndex === index) {
                  keysToRemove.push(id);
                }
              });
              keysToRemove.forEach(id => allGames.delete(id));

              // Add new games from this bound
              res.docs.forEach((doc) => {
                const data = doc.data() as Game & { _boundIndex?: number };
                data._boundIndex = index; // Track which bound this came from
                const { id } = data;
                allGames.set(id, data);
              });

              updateAllGames();
            } catch (error) {
              console.error(`Error processing public games for bound ${index}:`, error);
              dispatch(setGamesError(`Error loading public games: ${error}`));
            }
          },
          (err) => {
            console.error(`Error getting public games for bound ${index}:`, err);
            dispatch(setGamesError(`Error loading public games: ${err.message}`));
          },
        );
        listeners.push(unsubscribe);
      });
    }

    // Setup private games queries
    if (includePrivate && user?.uid) {
      const privateGameRef = query(
        collection(db, "games"),
        where("access", "==", "Private"),
        where(`allowedPlayers.${user.uid}`, "==", true),
      );

      const unsubscribe = onSnapshot(
        privateGameRef,
        (res) => {
          try {
            // Remove existing private games
            const keysToRemove: string[] = [];
            allGames.forEach((game, id) => {
              if (game.access === "Private") {
                keysToRemove.push(id);
              }
            });
            keysToRemove.forEach(id => allGames.delete(id));

            // Add new private games
            res.docs.forEach((doc) => {
              const data = doc.data() as Game;
              const { id } = data;
              allGames.set(id, data);
            });

            updateAllGames();
          } catch (error) {
            console.error("Error processing private games:", error);
            dispatch(setGamesError(`Error loading private games: ${error}`));
          }
        },
        (err) => {
          console.error("Error getting private games:", err);
          dispatch(setGamesError(`Error loading private games: ${err.message}`));
        },
      );
      listeners.push(unsubscribe);
    }

    // If no queries were set up, just set empty games
    if (listeners.length === 0) {
      dispatch(setAllGames({}));
    }

    // Cleanup function
    return () => {
      listeners.forEach((fn) => fn());
      allGames.clear();
    };
  }, [user, country, lat, lng, distance, includePublic, includePrivate, dispatch]);
};

export default useUnifiedGameList;